import { Box, type BoxProps } from "@mui/material";
import React, { ReactNode } from "react";

import { useLazyLoadItems } from "./use-lazy-load-items";

export interface LazyListProps extends Omit<BoxProps, "children"> {
  /** Total number of items in the list */
  itemCount: number;
  /** Items to display per page on scroll */
  itemsPerPage?: number;
  /** Threshold multiplier from viewport height to trigger load (default: 2) */
  scrollThreshold?: number;
  /** Children function that receives slice indices */
  children: (sliceStart: number, sliceEnd: number) => ReactNode;
}

/**
 * A component that enables lazy loading for lists.
 * Renders items progressively as the user scrolls.
 *
 * @example
 * <LazyList itemCount={data.length} itemsPerPage={20}>
 *   {(start, end) => (
 *     <>
 *       {data.slice(start, end).map(item => (
 *         <div key={item.id}>{item.name}</div>
 *       ))}
 *     </>
 *   )}
 * </LazyList>
 */
export const LazyList = React.forwardRef<HTMLDivElement, LazyListProps>(
  (
    {
      itemCount,
      itemsPerPage = 20,
      scrollThreshold = 2,
      children,
      sx,
      ...props
    },
    ref
  ) => {
    const { containerRef, itemsToDisplay, maxHeight } = useLazyLoadItems(
      itemCount,
      {
        itemsPerPage,
        scrollThreshold,
      }
    );

    // Merge refs
    const mergedRef = React.useCallback(
      (node: HTMLDivElement) => {
        containerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [containerRef, ref]
    );

    return (
      <Box
        ref={mergedRef}
        sx={{
          overflow: "auto",
          ...sx,
          maxHeight: maxHeight,
        }}
        {...props}
      >
        {children(0, itemsToDisplay)}
      </Box>
    );
  }
);

LazyList.displayName = "LazyList";
